/** The colour theme: light unless the person chooses Dark.
 *
 * **Light is the default, whatever the device prefers** (P13, KD reviewing the real app on a
 * dark-mode computer: it must open white with the teal rail). There is no "match my device" choice
 * and no `prefers-color-scheme` block in `tokens.css`; the only way to dark is Dark in My settings.
 *
 * **Stored per viewer, on this browser, and nothing more.** There is no theme field on the profile
 * API (`/auth/profile` carries units, locale and time zone, and those are kept per workspace); a
 * theme is a property of the screen being looked at rather than of the person's account, and adding a
 * server field for it would be a backend change this stage does not make. `localStorage` is a
 * convenience that throws in a private window and with site data blocked (`railPrefs.ts` says the
 * same), so every access is inside a `try` and "light" is what a failed read means. A failed write
 * only means the choice lasts until the tab closes. A value stored by the earlier three-way picker
 * (`system`) is not a choice any more and reads as light.
 *
 * **How it reaches CSS**: `<html data-theme>` is always `light` or `dark`, and `tokens.css` redefines
 * colour only under `[data-theme="dark"]`. `public/theme-init.js` does the same read before first
 * paint so a reader who chose dark is not shown a white flash; this module is the same logic for
 * after load. The storage key is shared with that file and `theme.test.ts` asserts it.
 */

import { useCallback, useEffect, useState } from "react";

export const THEME_KEY = "emitiq.theme";

export const THEME_CHOICES = ["light", "dark"] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === "string" && (THEME_CHOICES as readonly string[]).includes(value);
}

export function readTheme(): ThemeChoice {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return isThemeChoice(value) ? value : "light";
  } catch {
    return "light";
  }
}

export function writeTheme(choice: ThemeChoice): void {
  try {
    if (choice === "light") window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    // Private mode or blocked storage: the choice is not remembered, and the page still follows it.
  }
}

/** Put the choice on `<html>`. The attribute is always present, so nothing depends on the device. */
export function applyTheme(
  choice: ThemeChoice,
  root: HTMLElement = document.documentElement,
): void {
  root.setAttribute("data-theme", choice);
}

/** Read and apply the stored choice. Called once from `main.tsx`; harmless if `theme-init.js` ran. */
export function applyStoredTheme(): ThemeChoice {
  const choice = readTheme();
  applyTheme(choice);
  return choice;
}

/** The choice and a setter that applies and stores it. Follows another tab's change. */
export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== null && event.key !== THEME_KEY) return;
      const next = readTheme();
      applyTheme(next);
      setChoice(next);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const choose = useCallback((next: ThemeChoice) => {
    writeTheme(next);
    applyTheme(next);
    setChoice(next);
  }, []);

  return [choice, choose];
}
